import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/query";
import { AuthProvider, useAuth, homeForRole } from "@/lib/auth";
import { PrefsProvider } from "@/lib/prefs";
import { ProtectedRoute, BareProtectedRoute } from "@/components/ProtectedRoute";
import { Loading } from "@/components/brand";

import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Preferences from "@/pages/Preferences";

import Discover from "@/pages/member/Discover";
import Classes from "@/pages/member/Classes";
import Trainers from "@/pages/member/Trainers";
import Bookings from "@/pages/member/Bookings";
import ClassDetail from "@/pages/ClassDetail";
import TrainerDetail from "@/pages/TrainerDetail";
import BookPrivate from "@/pages/BookPrivate";
import BookingDetail from "@/pages/BookingDetail";
import Complete from "@/pages/Complete";
import Challenges from "@/pages/Challenges";
import ChallengeDetail from "@/pages/ChallengeDetail";
import Shop from "@/pages/Shop";
import Orders from "@/pages/Orders";
import Plans from "@/pages/Plans";
import Messages from "@/pages/Messages";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import Live from "@/pages/Live";

import TrainerDashboard from "@/pages/trainer/Dashboard";
import TrainerSchedule from "@/pages/trainer/Schedule";
import TrainerEarnings from "@/pages/trainer/Earnings";
import TrainerProfileEdit from "@/pages/trainer/TrainerProfile";
import TrainerChallenges from "@/pages/trainer/TrainerChallenges";

import AdminOverview from "@/pages/admin/Overview";
import AdminTrainers from "@/pages/admin/Trainers";
import AdminUsers from "@/pages/admin/Users";
import AdminBookings from "@/pages/admin/Bookings";
import AdminChallenges from "@/pages/admin/Challenges";
import AdminOrders from "@/pages/admin/Orders";
import AdminRates from "@/pages/admin/Rates";

const MEMBER = ["member", "admin"];

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loading /></div>;
  if (user) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Welcome /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/preferences" element={<Preferences />} />

      {/* Member + shared */}
      <Route path="/discover" element={<ProtectedRoute roles={MEMBER}><Discover /></ProtectedRoute>} />
      <Route path="/classes" element={<ProtectedRoute roles={MEMBER}><Classes /></ProtectedRoute>} />
      <Route path="/trainers" element={<ProtectedRoute roles={MEMBER}><Trainers /></ProtectedRoute>} />
      <Route path="/trainers/:id" element={<ProtectedRoute><TrainerDetail /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute roles={MEMBER}><Bookings /></ProtectedRoute>} />
      <Route path="/class/:id" element={<ProtectedRoute><ClassDetail /></ProtectedRoute>} />
      <Route path="/book-private/:trainerId" element={<ProtectedRoute roles={MEMBER}><BookPrivate /></ProtectedRoute>} />
      <Route path="/booking/:id" element={<ProtectedRoute><BookingDetail /></ProtectedRoute>} />
      <Route path="/complete/:id" element={<ProtectedRoute><Complete /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      <Route path="/challenges/:id" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
      <Route path="/plans" element={<ProtectedRoute roles={MEMBER}><Plans /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/chat/:id" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/live/:id" element={<BareProtectedRoute><Live /></BareProtectedRoute>} />

      {/* Trainer */}
      <Route path="/trainer" element={<ProtectedRoute roles={["trainer", "admin"]}><TrainerDashboard /></ProtectedRoute>} />
      <Route path="/trainer/schedule" element={<ProtectedRoute roles={["trainer", "admin"]}><TrainerSchedule /></ProtectedRoute>} />
      <Route path="/trainer/earnings" element={<ProtectedRoute roles={["trainer", "admin"]}><TrainerEarnings /></ProtectedRoute>} />
      <Route path="/trainer/profile" element={<ProtectedRoute roles={["trainer", "admin"]}><TrainerProfileEdit /></ProtectedRoute>} />
      <Route path="/trainer/challenges" element={<ProtectedRoute roles={["trainer", "admin"]}><TrainerChallenges /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminOverview /></ProtectedRoute>} />
      <Route path="/admin/trainers" element={<ProtectedRoute roles={["admin"]}><AdminTrainers /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/bookings" element={<ProtectedRoute roles={["admin"]}><AdminBookings /></ProtectedRoute>} />
      <Route path="/admin/challenges" element={<ProtectedRoute roles={["admin"]}><AdminChallenges /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute roles={["admin"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/rates" element={<ProtectedRoute roles={["admin"]}><AdminRates /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PrefsProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </PrefsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
